import React, { useState, useEffect } from 'react';
import { Settings, Link2, CheckCircle2, AlertCircle, Github, Trello, Key, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const IntegrationSettings = ({ project }) => {
  const [jiraConfig, setJiraConfig] = useState({ url: '', apiKey: '', project_key: '', enabled: false });
  const [azureConfig, setAzureConfig] = useState({ organization: '', project: '', pat: '', enabled: false });
  const [serviceNowConfig, setServiceNowConfig] = useState({ instance: '', username: '', password: '', enabled: false });
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [loading, setLoading] = useState({});
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchIntegrationStatus();
  }, [project.id]);

  const fetchIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/integrations/status`);
      setIntegrationStatus(response.data);
      
      // Update config states based on status
      if (response.data.jira.enabled) {
        setJiraConfig(prev => ({ ...prev, enabled: true }));
      }
      if (response.data.azure_devops.enabled) {
        setAzureConfig(prev => ({ ...prev, enabled: true }));
      }
      if (response.data.servicenow.enabled) {
        setServiceNowConfig(prev => ({ ...prev, enabled: true }));
      }
    } catch (error) {
      console.error('Error fetching integration status:', error);
    }
  };

  const handleJiraConfig = async () => {
    if (!jiraConfig.url || !jiraConfig.apiKey || !jiraConfig.project_key) {
      alert('Please fill in all fields');
      return;
    }
    
    setLoading({ ...loading, jira: true });
    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/integrations/jira/configure`,
        jiraConfig
      );
      
      if (response.data.success) {
        alert('Jira integration configured successfully!');
        await fetchIntegrationStatus();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Jira configuration error:', error);
      alert('Failed to configure Jira integration');
    } finally {
      setLoading({ ...loading, jira: false });
    }
  };

  const handleAzureConfig = async () => {
    if (!azureConfig.organization || !azureConfig.project || !azureConfig.pat) {
      alert('Please fill in all fields');
      return;
    }
    
    setLoading({ ...loading, azure: true });
    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/integrations/azure/configure`,
        azureConfig
      );
      
      if (response.data.success) {
        alert('Azure DevOps integration configured successfully!');
        await fetchIntegrationStatus();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Azure configuration error:', error);
      alert('Failed to configure Azure DevOps integration');
    } finally {
      setLoading({ ...loading, azure: false });
    }
  };

  const handleServiceNowConfig = async () => {
    if (!serviceNowConfig.instance || !serviceNowConfig.username || !serviceNowConfig.password) {
      alert('Please fill in all fields');
      return;
    }
    
    setLoading({ ...loading, servicenow: true });
    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/integrations/servicenow/configure`,
        serviceNowConfig
      );
      
      if (response.data.success) {
        alert('ServiceNow integration configured successfully!');
        await fetchIntegrationStatus();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error('ServiceNow configuration error:', error);
      alert('Failed to configure ServiceNow integration');
    } finally {
      setLoading({ ...loading, servicenow: false });
    }
  };

  const handleBulkSync = async (integrationType) => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/tasks/bulk-sync?integration_type=${integrationType}`
      );
      
      alert(`Bulk sync completed! Successful: ${response.data.successful}, Failed: ${response.data.failed}`);
    } catch (error) {
      console.error('Bulk sync error:', error);
      alert('Failed to sync tasks');
    } finally {
      setSyncing(false);
    }
  };

  const integrations = [
    {
      id: 'jira',
      name: 'Jira',
      icon: Trello,
      description: 'Sync tasks with Jira for project management',
      config: jiraConfig,
      setConfig: setJiraConfig,
      onSave: handleJiraConfig,
      color: 'blue',
      status: integrationStatus?.jira
    },
    {
      id: 'azure',
      name: 'Azure DevOps',
      icon: Github,
      description: 'Connect with Azure DevOps for work item tracking',
      config: azureConfig,
      setConfig: setAzureConfig,
      onSave: handleAzureConfig,
      color: 'indigo',
      status: integrationStatus?.azure_devops
    },
    {
      id: 'servicenow',
      name: 'ServiceNow',
      icon: Settings,
      description: 'Integrate with ServiceNow for change management',
      config: serviceNowConfig,
      setConfig: setServiceNowConfig,
      onSave: handleServiceNowConfig,
      color: 'green',
      status: integrationStatus?.servicenow
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link2 className="w-10 h-10" />
            <div>
              <h2 className="text-3xl font-bold">Integration Settings</h2>
              <p className="text-indigo-100 mt-1">Connect AURA with your existing tools</p>
            </div>
          </div>
          <button
            onClick={fetchIntegrationStatus}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Integrations */}
      <div className="grid lg:grid-cols-2 gap-6">
        {integrations.map((integration) => {
          const Icon = integration.icon;
          const colorClasses = {
            blue: 'from-blue-500 to-cyan-600',
            indigo: 'from-indigo-500 to-purple-600',
            green: 'from-green-500 to-emerald-600'
          };

          const isEnabled = integration.config.enabled || integration.status?.enabled;

          return (
            <div key={integration.id} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${colorClasses[integration.color]} rounded-xl flex items-center justify-center shadow-md`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{integration.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{integration.description}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <button
                    onClick={() => integration.setConfig({ ...integration.config, enabled: !integration.config.enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      integration.config.enabled ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        integration.config.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  {integration.status && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      integration.status.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {integration.status.status}
                    </span>
                  )}
                </div>
              </div>

              {integration.config.enabled && (
                <div className="mt-6 space-y-4 pt-4 border-t border-gray-200">
                  {integration.id === 'jira' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Jira URL</label>
                        <input
                          type="text"
                          value={integration.config.url}
                          onChange={(e) => integration.setConfig({ ...integration.config, url: e.target.value })}
                          placeholder="https://your-domain.atlassian.net"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Project Key</label>
                        <input
                          type="text"
                          value={integration.config.project_key}
                          onChange={(e) => integration.setConfig({ ...integration.config, project_key: e.target.value })}
                          placeholder="PROJ"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Key className="w-4 h-4 mr-2" />
                          API Key
                        </label>
                        <input
                          type="password"
                          value={integration.config.apiKey}
                          onChange={(e) => integration.setConfig({ ...integration.config, apiKey: e.target.value })}
                          placeholder="Enter your Jira API key"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </>
                  )}
                  {integration.id === 'azure' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Organization</label>
                        <input
                          type="text"
                          value={integration.config.organization}
                          onChange={(e) => integration.setConfig({ ...integration.config, organization: e.target.value })}
                          placeholder="your-org"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
                        <input
                          type="text"
                          value={integration.config.project}
                          onChange={(e) => integration.setConfig({ ...integration.config, project: e.target.value })}
                          placeholder="your-project"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Key className="w-4 h-4 mr-2" />
                          Personal Access Token
                        </label>
                        <input
                          type="password"
                          value={integration.config.pat}
                          onChange={(e) => integration.setConfig({ ...integration.config, pat: e.target.value })}
                          placeholder="Enter your PAT"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </>
                  )}
                  {integration.id === 'servicenow' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">ServiceNow Instance</label>
                        <input
                          type="text"
                          value={integration.config.instance}
                          onChange={(e) => integration.setConfig({ ...integration.config, instance: e.target.value })}
                          placeholder="your-instance.service-now.com"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                          <input
                            type="text"
                            value={integration.config.username}
                            onChange={(e) => integration.setConfig({ ...integration.config, username: e.target.value })}
                            placeholder="Username"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                          <input
                            type="password"
                            value={integration.config.password}
                            onChange={(e) => integration.setConfig({ ...integration.config, password: e.target.value })}
                            placeholder="Password"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="flex space-x-3">
                    <button 
                      onClick={integration.onSave}
                      disabled={loading[integration.id]}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {loading[integration.id] ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Configuring...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          <span>Save Configuration</span>
                        </>
                      )}
                    </button>
                    
                    {isEnabled && (
                      <button
                        onClick={() => handleBulkSync(integration.id)}
                        disabled={syncing}
                        className="px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium disabled:opacity-50 flex items-center space-x-2"
                      >
                        {syncing ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <ExternalLink className="w-5 h-5" />
                        )}
                        <span>Sync All</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isEnabled && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Integration Active</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">About Integrations</h4>
            <p className="text-sm text-blue-800 leading-relaxed">
              Integrations allow AURA to sync tasks, workitems, and changes with your existing project management tools. 
              All synced data maintains full traceability and can be monitored from the Analytics dashboard.
            </p>
            <ul className="text-sm text-blue-800 mt-3 space-y-1">
              <li>• <strong>Jira:</strong> Tasks are created as Jira issues in your specified project</li>
              <li>• <strong>Azure DevOps:</strong> Tasks sync as work items with proper status mapping</li>
              <li>• <strong>ServiceNow:</strong> High-priority changes generate change requests automatically</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
